import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import {
  AngularFirestore,
  AngularFirestoreDocument,
  AngularFirestoreCollection,
  DocumentChangeAction,
  Action,
  DocumentSnapshotDoesNotExist,
  DocumentSnapshotExists,
  DocumentReference,
} from '@angular/fire/firestore';
import { switchMap } from 'rxjs/operators';
import { User, AuthData } from './auth.service';

import { Observable, from, of, BehaviorSubject } from 'rxjs';
import { map, tap, take, mergeMap, expand, takeWhile } from 'rxjs/operators';

// import * as firebase from 'firebase';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import * as _ from 'lodash';

type CollectionPredicate<T> = string | AngularFirestoreCollection<T>;
type DocPredicate<T> = string | AngularFirestoreDocument<T>;

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private userSubject = new BehaviorSubject(null);
  user$ = this.userSubject.asObservable();

  get userId() {
    try {
      const user = firebase.auth().currentUser;
      if (!user) {
        console.log('userId() - have no current user', user);
        return null;
      }
      const userId = firebase.auth().currentUser.uid;
      return userId;
    } catch (error) {
      console.log('userId() - have no current user', error);
      return null;
    }
  }

  constructor(public afAuth: AngularFireAuth, public afs: AngularFirestore) {
    firebase.auth().onIdTokenChanged((user) => {
      if (user) {
        console.log('User is signed in or token was refreshed');
        this.userSubject.next(user);
      } else {
        console.log('no user');
        this.userSubject.next(null);
      }
    });
  }

  /// **************
  /// Authentication Functions
  /// **************

  async updateUser(userId, changes) {
    const userRef: AngularFirestoreDocument<User> = this.afs.doc(
      `users/${userId}`,
    );
    if (!userRef) {
      console.log(`updateUser() - null user detected for userId = '${userId}'`);
      return of(null).toPromise();
    }
    if (changes.archived) {
      console.log(`firebase service removing user(${userId}), ignore updates`);
      return of(null).toPromise();
    } else {
      console.log('firebase service updateUser()', changes);
      if (
        changes.phone &&
        (changes.phone.line === null || changes.phone.line === '')
      ) {
        console.log('attempt to clear user doc', changes);
        return of(null).toPromise();
      }
      return await userRef.set(changes, { merge: true });
    }
  }

  signout() {
    return firebase
      .auth()
      .signOut()
      .then(() => {
        console.log('Sign-out successful!');
      })
      .catch((error) => {
        console.log('Sign-out error.', error);
      });
  }

  deleteAccount() {
    firebase.auth().currentUser.delete();
  }

  /// **************
  /// Data Functions
  /// **************

  collection<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): AngularFirestoreCollection<T> {
    return typeof ref === 'string' ? this.afs.collection<T>(ref, queryFn) : ref;
  }

  doc<T>(ref: DocPredicate<T>): AngularFirestoreDocument<T> {
    return typeof ref === 'string' ? this.afs.doc<T>(ref) : ref;
  }

  docFromRef<T>(ref: DocumentReference): AngularFirestoreDocument<T> {
    return this.afs.doc<T>(ref);
  }

  docFromRef$<T>(ref: DocumentReference): Observable<T> {
    return this.docFromRef<T>(ref)
      .snapshotChanges()
      .pipe(
        map(
          (
            doc: Action<
              DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>
            >,
          ) => {
            return doc.payload.data() as T;
          },
        ),
      );
  }

  doc$<T>(ref: DocPredicate<T>): Observable<T> {
    return this.doc(ref)
      .snapshotChanges()
      .pipe(
        map(
          (
            doc: Action<
              DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>
            >,
          ) => {
            return doc.payload.data() as T;
          },
        ),
      );
  }

  docs$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<T[]> {
    return this.collection(ref, queryFn)
      .snapshotChanges()
      .pipe(
        map((docs: DocumentChangeAction<T>[]) => {
          return docs.map((a: DocumentChangeAction<T>) =>
            a.payload.doc.data(),
          ) as T[];
        }),
      );
  }

  /// with Ids
  docsWithIds$<T>(
    ref: CollectionPredicate<T>,
    owned: boolean,
    order: string,
    queryFn?,
  ): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (owned) {
          if (user) {
            const userQueryFn = (queryRef) =>
              queryRef.where('uid', '==', user.uid);
            const finalFn = queryFn
              ? (finalRef) => userQueryFn(queryFn(finalRef))
              : userQueryFn;
            return this.collection(ref, finalFn)
              .snapshotChanges()
              .pipe(
                map((actions: DocumentChangeAction<T>[]) => {
                  const items = actions.map((a: DocumentChangeAction<T>) => {
                    const data: Object = a.payload.doc.data() as T;
                    const id = a.payload.doc.id;
                    return { id, ...data };
                  });
                  // const sorted = items;
                  const sorted = _.sortBy(items, order);
                  return sorted;
                }),
              );
          } else {
            return [];
          }
        } else {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                return actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const id = a.payload.doc.id;
                  return { id, ...data };
                });
              }),
            );
        }
      }),
    );
  }

  ownedDocsWithIds$<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          const userQueryFn = (queryRef) =>
            queryRef.where('uid', '==', user.uid);
          const finalFn = queryFn
            ? (finalRef) => userQueryFn(queryFn(finalRef))
            : userQueryFn;
          return this.collection(ref, finalFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const id = a.payload.doc.id;
                  return { id, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  teamsWithIds$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          const userQueryFn = (queryRef) =>
            queryRef.where('teamStaff', 'array-contains', user.uid);
          const finalFn = queryFn
            ? (finalRef) => userQueryFn(queryFn(finalRef))
            : userQueryFn;
          return this.collection(ref, finalFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const teamId = a.payload.doc.id;
                  return { teamId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  iapsWithIds$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const iapId = a.payload.doc.id;
                  return { iapId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  membersWithIds$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const memberId = a.payload.doc.id;
                  return { memberId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  messagesWithIds$<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const messageId = a.payload.doc.id;
                  return { messageId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  repliesWithIds$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const replyId = a.payload.doc.id;
                  return { replyId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  numberPoolWithIds$<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): Observable<any[]> {
    console.log('numberPoolWithId');
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection('number-pool', queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const numberId = a.payload.doc.id;
                  return { numberId, ...data };
                });

                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  notificationWithIds$<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): Observable<any[]> {
    console.log('notificationWithIds');
    console.log(queryFn);
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const notificationId = a.payload.doc.id;
                  return { notificationId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  recipientsWithIds$<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): Observable<any[]> {
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection(ref, queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const memberId = a.payload.doc.id;
                  return { memberId, ...data };
                });
                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  userStaffWithIds$<T>(
    ref: CollectionPredicate<T>,
    queryFn?,
  ): Observable<any[]> {
    console.log('teamStaffWithIds');
    return this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.collection('team-staff', queryFn)
            .snapshotChanges()
            .pipe(
              map((actions: DocumentChangeAction<T>[]) => {
                const items = actions.map((a: DocumentChangeAction<T>) => {
                  const data: Object = a.payload.doc.data() as T;
                  const memberId = a.payload.doc.id;
                  return { memberId, ...data };
                });

                return items;
              }),
            );
        } else {
          return [];
        }
      }),
    );
  }

  /// **************
  /// Write Data
  /// **************

  /// Firebase Server Timestamp
  get timestamp() {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  async set<T>(ref: DocPredicate<T>, data: any): Promise<void> {
    const user = await this.afAuth.currentUser;
    if (!user) {
      console.log('bad user');
    }
    data.uid = user.uid;
    const timestamp = this.timestamp;
    return this.doc(ref).set({
      ...data,
      updatedAt: timestamp,
      createdAt: timestamp,
    });
  }

  update<T>(ref: DocPredicate<T>, data: any): Promise<void> {
    return this.doc(ref).update({
      ...data,
      updatedAt: this.timestamp,
    });
  }

  async delete<T>(ref: DocPredicate<T>): Promise<void> {
    return await this.doc(ref).delete();
  }

  async add<T>(
    ref: CollectionPredicate<T>,
    data,
  ): Promise<firebase.firestore.DocumentReference> {
    const user = await this.afAuth.currentUser;
    if (!user) {
      // JL_TODO: Can we trap this abd report to Crashlytics?
      console.log('bad user');
    }
    data.uid = user.uid;
    const timestamp = this.timestamp;
    return this.collection(ref).add({
      ...data,
      updatedAt: timestamp,
      createdAt: timestamp,
    });
  }

  geopoint(lat: number, lng: number): firebase.firestore.GeoPoint {
    return new firebase.firestore.GeoPoint(lat, lng);
  }

  /// If doc exists update, otherwise set
  upsert<T>(ref: DocPredicate<T>, data: any): Promise<void> {
    const doc = this.doc(ref).snapshotChanges().pipe(take(1)).toPromise();

    return doc.then(
      (
        snap: Action<DocumentSnapshotDoesNotExist | DocumentSnapshotExists<T>>,
      ) => {
        return snap.payload.exists
          ? this.update(ref, data)
          : this.set(ref, data);
      },
    );
  }

  /// **************
  /// Inspect Data
  /// **************

  inspectDoc(ref: DocPredicate<any>): void {
    const tick = new Date().getTime();
    this.doc(ref)
      .snapshotChanges()
      .pipe(
        take(1),
        tap(
          (
            d: Action<
              DocumentSnapshotDoesNotExist | DocumentSnapshotExists<any>
            >,
          ) => {
            const tock = new Date().getTime() - tick;
            console.log(`Loaded Document in ${tock}ms`, d);
          },
        ),
      )
      .subscribe();
  }

  inspectCol(ref: CollectionPredicate<any>): void {
    const tick = new Date().getTime();
    this.collection(ref)
      .snapshotChanges()
      .pipe(
        take(1),
        tap((c: DocumentChangeAction<any>[]) => {
          const tock = new Date().getTime() - tick;
          console.log(`Loaded Collection in ${tock}ms`, c);
        }),
      )
      .subscribe();
  }

  /// **************
  /// Create and read doc references
  /// **************

  /// create a reference between two documents
  connect(host: DocPredicate<any>, key: string, doc: DocPredicate<any>) {
    return this.doc(host).update({ [key]: this.doc(doc).ref });
  }

  /// returns a documents references mapped to AngularFirestoreDocument
  docWithRefs$<T>(ref: DocPredicate<T>) {
    return this.doc$(ref).pipe(
      map((doc: T) => {
        for (const k of Object.keys(doc)) {
          if (doc[k] instanceof firebase.firestore.DocumentReference) {
            doc[k] = this.doc(doc[k].path);
          }
        }
        return doc;
      }),
    );
  }

  /// **************
  /// Atomic batch example
  /// **************

  /// Just an example, you will need to customize this method.
  atomic() {
    const batch = firebase.firestore().batch();
    /// add your operations here

    const itemDoc = firebase.firestore().doc('items/myCoolItem');
    const userDoc = firebase.firestore().doc('users/userId');

    const currentTime = this.timestamp;

    batch.update(itemDoc, { timestamp: currentTime });
    batch.update(userDoc, { timestamp: currentTime });

    /// commit operations
    return batch.commit();
  }

  /**
   * Delete a collection, in batches of batchSize. Note that this does
   * not recursively delete subcollections of documents in the collection
   * from: https://github.com/AngularFirebase/80-delete-firestore-collections/blob/master/src/app/firestore.service.ts
   */
  deleteCollection(path: string, batchSize: number): Observable<any> {
    const source = this.deleteBatch(path, batchSize);

    // expand will call deleteBatch recursively until the collection is deleted
    return source.pipe(
      expand((val) => this.deleteBatch(path, batchSize)),
      takeWhile((val) => val > 0),
    );
  }

  // Detetes documents as batched transaction
  private deleteBatch(path: string, batchSize: number): Observable<any> {
    const colRef = this.afs.collection(path, (ref) =>
      ref.orderBy('__name__').limit(batchSize),
    );

    return colRef.snapshotChanges().pipe(
      take(1),
      mergeMap((snapshot: DocumentChangeAction<{}>[]) => {
        // Delete documents in a batch
        const batch = this.afs.firestore.batch();
        snapshot.forEach((doc) => {
          batch.delete(doc.payload.doc.ref);
        });

        return from(batch.commit()).pipe(map(() => snapshot.length));
      }),
    );
  }

  async mockTeamCreate(team) {
    console.log('firebase service mockTeamCreate()', team);
    if (team.teamId) {
      delete team.teamId;
    }
    // const teamRef: AngularFirestoreDocument<any> = this.afs.doc(
    //   `teams/${team.teamId}`,
    // );
    const timestamp = this.timestamp;
    // team.teamCreatedAt = timestamp;
    // team.teamUpdatedAt = timestamp;
    // return await teamRef.set(team, { merge: true });

    this.collection('teams').add({
      ...team,
      teamUpdatedAt: timestamp,
      teamCreatedAt: timestamp,
    });
  }
}
